import { DoorOpen, UserRound, UsersRound } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import type { Lesson } from '../types'
import { subjectStyle } from '../week'

/** What the secondary lines of a lesson show, depending on whose timetable it is. */
export type LessonView = 'section' | 'teacher' | 'room'

interface LessonCardProps {
  lesson: Lesson
  view: LessonView
  chip?: ReactNode
  compact?: boolean
  className?: string
}

function Line({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="flex min-w-0 items-center gap-1 text-xs text-ink-soft [&_svg]:size-3 [&_svg]:shrink-0 [&_svg]:text-muted">
      {icon}
      <span className="truncate">{children}</span>
    </span>
  )
}

/** A lesson tile: subject colour accent + subject name, then teacher / section / room. */
export function LessonCard({ lesson, view, chip, compact, className }: LessonCardProps) {
  const { t } = useTranslation('timetable')
  const style = subjectStyle(lesson.subject.color)
  return (
    <div
      className={cn(
        'relative flex h-full min-h-16 flex-col gap-0.5 rounded-control border border-l-4 border-line px-2.5 py-2 text-left',
        !style && 'border-l-brand-400 bg-brand-50',
        className,
      )}
      style={style}
    >
      <span className="flex items-start justify-between gap-1">
        <span className={cn('font-semibold text-ink', compact ? 'text-xs' : 'text-[0.8125rem] leading-snug')}>
          {lesson.subject.name}
        </span>
        {chip}
      </span>
      {view !== 'section' ? <Line icon={<UsersRound />}>{lesson.section.name}</Line> : null}
      {view !== 'teacher' ? (
        <Line icon={<UserRound />}>
          {lesson.teacher?.name ?? <span className="text-muted italic">{t('No teacher')}</span>}
        </Line>
      ) : null}
      {view !== 'room' && lesson.room ? <Line icon={<DoorOpen />}>{lesson.room.name}</Line> : null}
    </div>
  )
}
